import { Button, LinearProgress } from '@material-ui/core';
import { Attachment } from '@material-ui/icons';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Avatar, Step, StepConnector, StepLabel, Stepper } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { bytesToSize, duplicateNumberInArray } from '../../../../../utils/commonUtils';
import {
  isExtensionNumberValid,
  isPasswordValid,
  isRoomNumberValid,
} from '../../../../../utils/formUtils';
import { uploadTelephonyExtension } from '../../../../../utils/ServicesAPIUtils';
import { fetchAllTelephonyDetails } from '../../../servicesSlice';
import './Extension-Upload.scss';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { DOWNLOAD_ASSETS_URL } from '../../../../../env/Configurations';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';

const baseStyle = {
  width: '35%',
  height: 150,
  marginLeft: '33%',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 1,
  borderRadius: 10,
  borderColor: '#CCCCCC',
  borderStyle: 'dashed',
  backgroundColor: '#fff',
  color: '#CCCCCC',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  minHeight: 120,
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const ExtensionUploadModel = (props) => {
  const { onClose, hotelId } = props;
  const dispatch = useDispatch();

  const [tabValue, setTabValue] = useState(0);
  const [stepperActiveStep, setStepperActiveStep] = useState(0);
  const [uploadError, setUploadError] = useState(false);
  const [progress, setProgress] = useState(0);
  const [acceptedFile, setAcceptedFile] = useState(null);
  const [validFormatError, setValidFormatError] = useState(false);
  const [uploadedExtension, setUploadedExtension] = useState([]);
  const [tempExtension, setTempExtension] = useState([]);
  const [conflictExtension, setConflictExtension] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const logo = '/assets/img/hudini-logo-black.png';

  useEffect(() => {
    if (tabValue === 0) setStepperActiveStep(0);
    if (tabValue === 1 || tabValue === 2 || tabValue === 3 || tabValue === 4)
      setStepperActiveStep(1);
    if (tabValue === 5) setStepperActiveStep(2);
  }, [tabValue]);

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
  };

  const onDropAccepted = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      handleDroppedFile(acceptedFiles[0]);
    }
  }, []);

  const onDocDelete = () => {
    setAcceptedFile(null);
    setUploadError(false);
    setValidFormatError(false);
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } =
    useDropzone({
      accept: '.csv,.xlsx,.xls,text/csv,application/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      multiple: false,
      noClick: true,
      onDrop: onDropAccepted,
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept],
  );

  const findDuplicateNumber = (extensionList) => {
    const extensionListKeysNames = Object.keys(extensionList[0]);
    const roomNumberList = extensionList.map((extension) => extension[extensionListKeysNames[0]]);
    const extensionNumberList = extensionList.map(
      (extension) => extension[extensionListKeysNames[1]],
    );
    const isRoomNumberDuplicate = duplicateNumberInArray(roomNumberList);
    const isExtensionNumberDuplicate = duplicateNumberInArray(extensionNumberList);
    return isRoomNumberDuplicate || isExtensionNumberDuplicate;
  };

  useEffect(() => {
    if (acceptedFile) {
      setUploadError(false);
      setValidFormatError(false);
      setValidationErrors([]);
      if (tempExtension.length > 0) {
        const errors = [];
        tempExtension.forEach((extension) => {
          const rowNum = extension.__rowNum || '?';
          const rowErrors = [];
          if (!extension?.roomNumber?.trim()) rowErrors.push('"Room Number" is empty');
          if (!extension?.extensionNumber?.trim()) rowErrors.push('"Extension Number" is empty');
          if (!extension?.password?.trim()) rowErrors.push('"Password" is empty');
          if (extension?.roomNumber?.trim() && !isRoomNumberValid(extension.roomNumber)) rowErrors.push('"Room Number" is invalid (max 6 chars, alphanumeric)');
          if (extension?.extensionNumber?.trim() && !isExtensionNumberValid(extension.extensionNumber)) rowErrors.push('"Extension Number" is invalid (max 6 digits)');
          if (extension?.password?.trim() && !isPasswordValid(extension.password)) rowErrors.push('"Password" is invalid (max 8 chars, alphanumeric)');
          if (rowErrors.length > 0) {
            errors.push(`Row ${rowNum}: ${rowErrors.join(', ')}`);
          }
        });
        if (errors.length > 0) {
          setValidationErrors(errors);
          setUploadError(true);
          setValidFormatError(true);
        } else {
          const seenRooms = new Set();
          const seenExtensions = new Set();
          const dupErrors = [];
          const deduplicatedList = tempExtension.filter((extension) => {
            const roomKey = extension.roomNumber?.trim();
            const extKey = extension.extensionNumber?.trim();
            const isDup = seenRooms.has(roomKey) || seenExtensions.has(extKey);
            if (isDup) {
              const rowNum = extension.__rowNum || '?';
              const reasons = [];
              if (seenRooms.has(roomKey)) reasons.push(`room number '${roomKey}'`);
              if (seenExtensions.has(extKey)) reasons.push(`extension number '${extKey}'`);
              dupErrors.push(`Row ${rowNum}: Duplicate ${reasons.join(' and ')} within file`);
              return false;
            }
            seenRooms.add(roomKey);
            seenExtensions.add(extKey);
            return true;
          });
          if (dupErrors.length > 0) {
            dupErrors.forEach((e) => errors.push(e));
            setValidationErrors(errors);
            setUploadError(true);
            setValidFormatError(true);
          } else {
            setUploadedExtension(deduplicatedList);
            setProgress(100);
            setUploadError(false);
            setValidFormatError(false);
            setValidationErrors([]);
          }
        }
      } else {
        setValidationErrors(['No data rows found in the uploaded file.']);
        setUploadError(true);
      }
    }
  }, [tempExtension]);

  useEffect(() => {
    if (acceptedFile) {
      submitExtension(false, uploadedExtension);
    }
  }, [uploadedExtension]);

  const submitExtension = async (isReplacing, payloadExtensions) => {
    try {
      dispatch(stepUpLoaderCount());
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      setTabValue(3);
      const cleanExtensions = (payloadExtensions || []).map(({ __rowNum, ...rest }) => rest);
      const payload = {
        isReplace: isReplacing,
        hotelId: hotelId,
        extensions: cleanExtensions,
      };
      const res = await uploadTelephonyExtension(payload, language);
      if (res?.data?.addExtensions?.status === 'Success') {
        if (
          res?.data?.addExtensions?.duplicates &&
          res?.data?.addExtensions?.duplicates.length > 0
        ) {
          setTabValue(2);
          setConflictExtension(res.data.addExtensions.duplicates);
        } else {
          setTabValue(5);
        }
      } else {
        setTabValue(4);
      }
    } catch (e) {
      setTabValue(4);
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const sanitizeCellValue = (val) => {
    if (val == null) return '';
    return String(val)
      .replace(/\uFEFF/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/[\u200B-\u200D\u2060\uFFF9-\uFFFB]/g, '')
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      .replace(/[\u2018\u2019\u201A]/g, "'")
      .replace(/[\u201C\u201D\u201E]/g, '"')
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/\u2026/g, '...')
      .replace(/\r\n/g, ' ')
      .replace(/[\r\n]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const parseCSVText = (text) => {
    const rows = [];
    let current = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      const next = text[i + 1];
      if (inQuotes) {
        if (ch === '"') {
          if (next === '"') { field += '"'; i++; } else { inQuotes = false; }
        } else { field += ch; }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === ',') { current.push(field); field = ''; }
        else if (ch === '\r' && next === '\n') { current.push(field); field = ''; rows.push(current); current = []; i++; }
        else if (ch === '\n' || ch === '\r') { current.push(field); field = ''; rows.push(current); current = []; }
        else { field += ch; }
      }
    }
    current.push(field);
    if (current.some((f) => f.trim() !== '')) rows.push(current);
    return rows.filter((r) => r.some((f) => f.trim() !== ''));
  };

  const readFileData = async (fileObj) => {
    setProgress(50);
    try {
      let rawRows;
      try {
        const data = await fileObj.arrayBuffer();
        const readedData = XLSX.read(new Uint8Array(data), {
          type: 'array',
          cellStyles: false,
          cellFormula: false,
          cellHTML: false,
          cellDates: false,
        });
        const wsname = readedData.SheetNames[0];
        const ws = readedData.Sheets[wsname];
        rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: true });
      } catch (excelErr) {
        const text = await fileObj.text();
        rawRows = parseCSVText(text);
      }
      if (!Array.isArray(rawRows) || rawRows.length < 2) {
        setUploadError(true);
        return false;
      }
      const headerRow = rawRows[0];
      if (!headerRow || headerRow.length < 3) {
        setUploadError(true);
        return false;
      }
      const dataRows = rawRows.slice(1);
      const result = dataRows.map((row, i) => {
        const roomVal = sanitizeCellValue(String(row[0] ?? ''));
        const extVal = sanitizeCellValue(String(row[1] ?? ''));
        const passVal = sanitizeCellValue(String(row[2] ?? ''));
        return {
          __rowNum: i + 2,
          roomNumber: roomVal,
          extensionNumber: extVal,
          password: passVal,
        };
      }).filter((item) => item.roomNumber !== '' || item.extensionNumber !== '' || item.password !== '');

      if (result.length === 0) {
        setUploadError(true);
        return false;
      }

      const isTemplateInstructionRow = (row) => {
        const values = [row.roomNumber, row.extensionNumber, row.password];
        return values.some((v) => {
          const s = String(v || '').toLowerCase();
          return (
            s.startsWith('*') &&
            (s.includes('character limit') || s.includes('charcter limit') || s.includes('duplicate rooms') || s.includes('max records'))
          );
        });
      };
      const filteredItemList = result.filter((row) => !isTemplateInstructionRow(row));

      if (filteredItemList.length === 0) {
        setValidationErrors(['No data rows found in the uploaded file.']);
        setUploadError(true);
        setValidFormatError(true);
        return false;
      }
      setTempExtension(filteredItemList);
    } catch (e) {
      setUploadError(true);
      return false;
    }
  };

  const handleDroppedFile = async (fileObj) => {
    setProgress(0);
    setAcceptedFile(fileObj);
    await readFileData(fileObj);
  };

  const onCloseModal = (e) => {
    dispatch(fetchAllTelephonyDetails(hotelId));
    onClose(e);
  };
  return (
    <div className='d-flex upload-container'>
      <div className='row col-md-12'>
        <div className='upload-container-header mb-4 d-flex'>Upload Room Extensions</div>
        <div className='header-logo'>
          <img src={logo} alt='logo' />
        </div>
        <div className='upload-container-stepper'>
          <Stepper
            activeStep={stepperActiveStep}
            alternativeLabel
            connector={
              <StepConnector
                style={{
                  marginLeft: '-2.9%',
                  marginRight: '-2.9%',
                }}
              />
            }
          >
            <Step key='Template'>
              <StepLabel icon={tabValue === 0 ? <CircleOutlinedIcon /> : <CheckCircleIcon />}>
                Template
                {tabValue === 0 && (
                  <div className='stepper-top-arrow'>
                    <KeyboardArrowDownIcon />
                  </div>
                )}
              </StepLabel>
            </Step>
            <Step key='Upload'>
              <StepLabel
                icon={
                  tabValue === 5 ? (
                    <CheckCircleIcon />
                  ) : tabValue === 0 || tabValue === 1 || tabValue === 3 ? (
                    <CircleOutlinedIcon />
                  ) : (
                    <Avatar className='error-icon-avatar'>
                      <ErrorIcon className='error-icon' />
                    </Avatar>
                  )
                }
              >
                Upload
                {tabValue > 0 && tabValue < 5 && (
                  <div className='stepper-top-arrow'>
                    <KeyboardArrowDownIcon />
                  </div>
                )}
              </StepLabel>
            </Step>
          </Stepper>
        </div>
        {tabValue === 0 && (
          <div className='row col-md-12 mb-4 mt-4'>
            <div className='upload-container-title my-4'>Download Excel Template</div>
            <div className='upload-container-content'>
              <p>
                To minimize error while mapping, we have designed a template ,
                <br />
                Which ensures easy mapping of all the columns in the excel sheet to the required
                form fields.
                <br />
              </p>
              <p className='mt-2'>
                Download below the Excel Template for Room Extensions form page
              </p>
            </div>
            <div className='download-button'>
              <a
                className='download-button-white'
                href={DOWNLOAD_ASSETS_URL + '/cmsAssets/Extensions+-Upload++function+Template.xlsx'}
                download='Extension Template.xlsx'
              >
                <Button variant='contained' size='large' color='primary' className='col-md-2'>
                  <FileDownloadOutlinedIcon className='mx-2' />
                  Download Template
                </Button>
              </a>
            </div>
            <div className='skip-button'>
              <p className='col-md-12'>
                Already have the template?
                <a className='pointer mx-3' onClick={(e) => handleTabChange(1)}>
                  Skip This
                </a>
              </p>
            </div>
          </div>
        )}
        {tabValue === 1 && (
          <div className='row col-md-12 mb-4 mt-2'>
            <div className='upload-container-title my-4'>Upload your file</div>
            <div className='upload-container-content'>
              <p>
                Upload the filled excel template for Room Extensions. <br />
                Upload only in .csv, .xls or .xlsx formats
              </p>
            </div>
            {acceptedFile ? (
              <div className={'col-md-12 p-2 uploading-status ' + (uploadError ? 'error' : '')}>
                <div
                  className={
                    'd-flex justify-content-between align-items-center py-2 accepted-container ' +
                    (uploadError ? 'upload-error' : '')
                  }
                >
                  <div> {acceptedFile.name} </div>
                  <div>{acceptedFile.size && bytesToSize(acceptedFile.size)}</div>
                  {uploadError ? (
                    <div className='d-flex align-items-center'>
                      <div>Upload Failed</div>
                      <div>
                        <CancelIcon
                          className='pointer me-1'
                          onClick={(e) => onDocDelete()}
                        ></CancelIcon>
                      </div>
                    </div>
                  ) : (
                    <div className='upload-progress d-flex align-items-center'>
                      <div className='upload-progress-title'>Uploading</div>
                      <div className='upload-progress-bar'>
                        <LinearProgress variant='determinate' color='secondary' value={progress} />
                      </div>
                      <CancelIcon
                        className='pointer me-1'
                        onClick={(e) => onDocDelete()}
                      ></CancelIcon>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className='row pt-2'>
                <div className='img-drop-zone'>
                  <div {...getRootProps({ style })}>
                    <input {...getInputProps()} />
                    <div className='d-flex align-items-center'>
                      <Attachment />
                      <span className='ms-1'>
                        Drag and drop or
                        <span className='text-highlight pointer' onClick={open}>
                          Browse
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {uploadError && !validFormatError && (
              <div className='mt-4 upload-container-error'>
                There was some error while uploading the file.{' '}
                <span onClick={(e) => onDocDelete()} className='link-text text-red'>
                  Try Again
                </span>
              </div>
            )}
            {uploadError && validFormatError && (
              <>
                <div className='mt-4 upload-container-error'>
                  There was some error while uploading the file.{' '}
                </div>
                <div className='mt-4 upload-container-error'>
                  Please check if the data in the document satisfy the acceptance criteria and{' '}
                  <span onClick={(e) => onDocDelete()} className='link-text text-red'>
                    Try Again
                  </span>
                </div>
                {validationErrors && validationErrors.length > 0 && (
                  <div className='mt-3 upload-container-error' style={{ width: '80%', margin: '0 auto', maxHeight: '130px', overflowY: 'auto', textAlign: 'left', fontSize: '13px', background: '#fff5f5', border: '1px solid #e8bcbc', borderRadius: '6px', padding: '12px 16px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '6px', color: '#c0392b' }}>Issues found:</div>
                    <ul style={{ margin: 0, paddingLeft: '18px', listStyleType: 'disc' }}>
                      {validationErrors.map((err, idx) => (
                        <li key={idx} style={{ marginBottom: '3px', color: '#555' }}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {tabValue === 2 && (
          <div className='row col-md-12 mb-4 mt-2'>
            <div>
              <div className='upload-container-title my-4'>
                Replace extension for the following Rooms?
              </div>
              <div className='upload-container-content'>
                <p>
                  These Rooms already have extensions mapped to them on Elevate - Telephony.
                  <br />
                  Do you want to replace the extensions for these rooms? <br />
                  On selecting option No, the extensions for these rooms will not be uploaded to
                  elevate.
                </p>
              </div>
              <div className='duplicate-extension'>
                <div className='extension-overflow'>
                  {conflictExtension?.map((extension) => (
                    <div
                      className='p-3 mt-1 duplicate-extension-list d-flex justify-content-between'
                      key={conflictExtension.roomNumber}
                    >
                      <div key={extension.roomNumber}>{extension?.roomNumber}</div>
                      <div> Extension: {extension?.extensionNumber}</div>
                    </div>
                  ))}
                </div>
                <div className='col-md-12 pt-4 d-flex justify-content-center mt-auto mb-4'>
                  <div>
                    <Button
                      variant='outlined'
                      size='large'
                      className='px-5 py-2'
                      color='secondary'
                      onClick={(e) => {
                        onDocDelete();
                        setTabValue(1);
                      }}
                    >
                      No
                    </Button>
                    <Button
                      variant='contained'
                      size='large'
                      color='primary'
                      className='mx-4 py-2'
                      onClick={(e) => submitExtension(true, conflictExtension)}
                    >
                      Replace
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {tabValue === 3 && (
          <div className='row col-md-12 mb-4 mt-2'>
            <div className='row col-md-12 upload-success'>
              <div className='loading-icon'>
                <div id='circularG'>
                  <div id='circularG_1' className='circularG'></div>
                  <div id='circularG_2' className='circularG'></div>
                  <div id='circularG_3' className='circularG'></div>
                  <div id='circularG_4' className='circularG'></div>
                  <div id='circularG_5' className='circularG'></div>
                  <div id='circularG_6' className='circularG'></div>
                  <div id='circularG_7' className='circularG'></div>
                  <div id='circularG_8' className='circularG'></div>
                </div>
              </div>
              <div className='upload-container-title my-4'>Uploading</div>
              <div className='upload-container-content'>
                <p>File name: {acceptedFile.name}</p>
              </div>
            </div>
          </div>
        )}
        {tabValue === 4 && (
          <div className='row col-md-12 mb-4 mt-2'>
            <div className='row col-md-12 mb-4 mt-2'>
              <div className='upload-container-title my-4'>Unable to Upload document!</div>
              <div className='upload-container-content'>
                <p>
                  The Extension present in the Excel document are already mapped to other rooms,{' '}
                  <br />
                  Please cross check your document and re-upload the document with corrected
                  details.
                </p>
              </div>
              <div className='col-md-12 pt-4 d-flex justify-content-center mt-auto mb-4'>
                <div>
                  <Button
                    variant='outlined'
                    size='large'
                    className='px-5 py-2'
                    color='secondary'
                    onClick={(e) => {
                      onDocDelete();
                      setTabValue(1);
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant='contained'
                    size='large'
                    color='primary'
                    className='mx-4 py-2'
                    onClick={(e) => {
                      onDocDelete();
                      setTabValue(1);
                    }}
                  >
                    Re-Upload
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {tabValue === 5 && (
          <div className='row col-md-12 mb-4 mt-2'>
            <div className='row col-md-12 upload-success'>
              <div className='success-icon'>
                <CheckCircleIcon
                  style={{
                    fontSize: '65px',
                  }}
                />
              </div>
              <div className='upload-container-title my-4'>Uploaded</div>
              <div className='upload-container-content'>
                <p>File name: {acceptedFile.name}</p>
              </div>
              <div className='ok-button'>
                <Button
                  variant='contained'
                  size='large'
                  color='primary'
                  className='px-5 py-2'
                  onClick={(e) => onCloseModal(e)}
                >
                  Ok
                </Button>
              </div>
              <div className='return-telephony mt-2'>
                <p>Return to Telephony - Directory</p>
              </div>
            </div>
          </div>
        )}
        <div className='col-md-12 mt-4 d-flex justify-content-between mt-auto mb-4 bottom-button'>
          <div>
            {(tabValue === 1 || tabValue === 2) && (
              <Button
                variant='outlined'
                size='large'
                color='secondary'
                onClick={(e) => {
                  onDocDelete();
                  handleTabChange(tabValue - 1);
                }}
              >
                Back
              </Button>
            )}
            {(tabValue === 0 || tabValue === 1) && (
              <Button
                variant='outlined'
                size='large'
                color='secondary'
                className='mx-4'
                onClick={(e) => onCloseModal(e)}
              >
                Cancel
              </Button>
            )}
          </div>
          <div>
            {tabValue === 0 && (
              <Button
                variant='contained'
                size='large'
                color='primary'
                onClick={(e) => handleTabChange(tabValue + 1)}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtensionUploadModel;
